import React, { useRef } from "react";
import CopyImageChip from "./CopyImageChip";

/**
 * One Group Stage card: dark header, a week column per cup week, and a
 * points-for total. Qualified rows are tinted green, wildcards mustard.
 */
const CupGroupCard = ({ group, groupWeeks, weekLabels }) => {
    const ref = useRef(null);
    const weeks = groupWeeks || [];
    const numericColumnCount = weeks.length + 1;
    const teamColumnWidth = weeks.length >= 5 ? "30%" : "36%";
    const numericColumnWidth = `${(100 - parseFloat(teamColumnWidth)) / numericColumnCount}%`;

    const weekHeader = (week) => {
        const label = weekLabels?.[week] || weekLabels?.[String(week)];
        if (!label) return `W${week}`;
        // Compact plain "Week N" labels; keep custom ones (e.g. playoff names).
        const match = /^week\s*(\d+)$/i.exec(label);
        return match ? `W${match[1]}` : label;
    };

    return (
        <div
            ref={ref}
            className="overflow-hidden rounded-2xl border border-border bg-surface shadow-card"
        >
            <div className="flex items-center justify-between bg-gradient-to-r from-forest to-[#2a4a36] px-4 py-3">
                <h4 className="font-display text-[17px] font-black text-white">
                    {group.name}
                </h4>
                <CopyImageChip
                    targetRef={ref}
                    fileName={`league-cup-${group.name.toLowerCase().replace(/\s+/g, "-")}`}
                    label=""
                    variant="dark"
                />
            </div>
            <div>
                <table className="w-full table-fixed border-collapse font-sans text-[13px]">
                    <colgroup>
                        <col style={{ width: teamColumnWidth }} />
                        {weeks.map((week) => (
                            <col key={week} style={{ width: numericColumnWidth }} />
                        ))}
                        <col style={{ width: numericColumnWidth }} />
                    </colgroup>
                    <thead>
                        <tr>
                            <th className="border-b-[1.5px] border-[#e4dfd2] py-2 pl-3.5 pr-1.5 text-left text-[9.5px] font-extrabold uppercase tracking-wider text-text-muted">
                                Team
                            </th>
                            {weeks.map((week) => (
                                <th
                                    key={week}
                                    className="whitespace-nowrap border-b-[1.5px] border-[#e4dfd2] px-1.5 py-2 text-right text-[9.5px] font-extrabold uppercase tracking-wider text-text-muted"
                                >
                                    {weekHeader(week)}
                                </th>
                            ))}
                            <th className="border-b-[1.5px] border-[#e4dfd2] px-1.5 py-2 pr-3.5 text-right text-[9.5px] font-extrabold uppercase tracking-wider text-text-muted">
                                PF
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {(group.table || []).map((row) => (
                            <tr
                                key={row.provider_team_id}
                                className={
                                    row.wildcard
                                        ? "bg-mustard-light"
                                        : row.qualified
                                        ? "bg-green-light"
                                        : ""
                                }
                            >
                                <td className="border-b border-[#efebe0] py-2 pl-3.5 pr-1.5 [tr:last-child_&]:border-b-0">
                                    <div className="break-words text-[13px] font-extrabold leading-tight text-ds-text">
                                        {row.owner_name || row.team_name}
                                    </div>
                                    {row.owner_name &&
                                        row.team_name && (
                                            <div className="break-words text-[10.5px] font-semibold leading-snug text-text-muted">
                                                {row.team_name}
                                            </div>
                                        )}
                                </td>
                                {weeks.map((week) => {
                                    const score =
                                        row.week_scores?.[week] ??
                                        row.week_scores?.[String(week)];
                                    return (
                                        <td
                                            key={week}
                                            className="border-b border-[#efebe0] px-1.5 py-2 text-right tabular-nums text-text-mid [tr:last-child_&]:border-b-0"
                                        >
                                            {score != null
                                                ? Number(score).toFixed(1)
                                                : "—"}
                                        </td>
                                    );
                                })}
                                <td className="border-b border-[#efebe0] px-1.5 py-2 pr-3.5 text-right font-extrabold tabular-nums text-ds-text [tr:last-child_&]:border-b-0">
                                    {Number(row.group_points_for || 0).toFixed(2)}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default CupGroupCard;
